import client from 'axios'
import _ from 'lodash'
import ProxyPolyfill from 'proxy-polyfill/src/proxy'

const axios = client.create()

const getCSRFToken = () => {
  const token = _.find(document.getElementsByTagName('meta'), (meta) => {
    return meta.name === 'csrf-token'
  })

  return token ? token.content : null
}

const getBearerToken = () => {
  if (window.jwtProxy && window.jwtProxy.state.authenticated) {
    return `Bearer ${window.jwtProxy.state.rawToken}`;
  }

  return '';
}

axios.interceptors.request.use( (config) => {
  let { headers } = config

  return {
    ...config,
    headers: Object.assign(
      headers || {}, {
        'X-CSRF-Token': getCSRFToken(),
        'Authorization': getBearerToken()
      }
    )
  }
});

const bindProxy = (onMethods, handler) => {
  let binding = () => {}
  let proxy;

  if (typeof Proxy !== 'function') {
    onMethods.forEach(function(prop) {
      Object.defineProperty(binding, prop, { value: undefined })
    })

    proxy = ProxyPolyfill()
  }
  else {
    proxy = Proxy
  }

  return new proxy(binding, handler)
}

class Resource {
  static get methods() {
    return [ 'index', 'find', 'create', 'update', 'patch', 'delete' ];
  }

  constructor(baseURL, config, placeholders = {}) {
    this.baseURL = baseURL
    this.placeholders = placeholders

    this._routes = this._createResourcePaths(config)
    this._endpoints = [
      ...this.constructor.methods,
      ...this._createCollectionEndpoints(config),
      ...this._createMemberEndpoints(config)
    ]
  }

  get endpoints() { return this._endpoints }
  get routes() { return this._routes }

  _createResourcePaths({ name, path }) {
    const resourcePath = path || name
    const url = `${this.baseURL}/${resourcePath}`

    var paths = {
      resourcesURL: this._interpolate(url)
    }

    paths.resourceURL = (id) => id ? `${paths.resourcesURL}/${id}` : paths.resourcesURL

    return paths
  }

  index(config={}) {
    return axios.get(this.routes.resourcesURL, config)
  }

  find({ id }, config={}) {
    return axios.get(this.routes.resourceURL(id), config)
  }

  create(payload, config={}) {
    return axios.post(this.routes.resourcesURL, payload, config)
  }

  update(payload, config={}) {
    return axios.put(this.routes.resourceURL(payload.id), payload, config)
  }

  patch({ id }, payload, config={}) {
    return axios.patch(this.routes.resourceURL(id), payload, config)
  }

  delete({ id }, config={}) {
    return axios.delete(this.routes.resourceURL(id), config)
  }

  _interpolate(string) {
    var result = string

    _.forIn(this.placeholders, (v, k) => result = _.replace(result, `:${k}`, v))

    return result
  }

  _createCollectionEndpoints({ collection }) {
    var endpoints = []
    if (!collection) { return endpoints }

    _.forEach(collection, ({ name, method }) => {
      endpoints.push(name)

      Object.defineProperty(this, name, {
        get: () => {
          let yielder = this

          return bindProxy([name, 'path'], {
            get: (target, prop, receiver) => {
              switch (prop) {
              case "path":
                return `${yielder.routes.resourcesURL}/${name}`
              default:
                return yielder[prop]
              }
            },
            apply: (target, thisArg, argumentsList) => {
              const url = `${yielder.routes.resourcesURL}/${name}`
              return yielder._requestCustomEndpoint(url, method, argumentsList[0])
            }
          })
        }
      })
    })

    return endpoints
  }

  _createMemberEndpoints({ member }) {
    var endpoints = []
    if (!member) { return endpoints }

    _.forEach(member, ({ name, method }) => {
      endpoints.push(name)

      Object.defineProperty(this, name, {
        get: () => {
          let yielder = this

          return bindProxy([name, 'path'], {
            get: (target, prop, receiver) => {
              switch (prop) {
              case "path":
                return `${yielder.routes.resourcesURL}/${name}`
              default:
                return yielder[prop]
              }
            },
            apply: (target, thisArg, argumentsList) => {
              const { id, ...config } = argumentsList[0] || {}
              const url = `${yielder.routes.resourceURL(id)}/${name}`
              return yielder._requestCustomEndpoint(url, method, config || {})
            }
          })
        }
      })
    })

    return endpoints
  }

  _requestCustomEndpoint(url, method, config = {}) {
    return axios({
      method: method || 'GET',
      url: url,
      ...config
    })
  }
}

export default class API {
  constructor(url) {
    this.url = url
    this.resources = {}
  }

  createEntity(entity) {
    let url = this.url

    this.resources[entity.name] = {}
    this.resources[entity.name].config = entity

    const resource = new Resource(url, entity)

    return bindProxy(resource.endpoints.concat(['endpoints', 'routes']), {
      get: (target, prop, receiver) => {
        // const resource = new Resource(url, entity)
        return resource[prop]
      },

      apply: (target, thisArg, argumentsList) => {
        return new Resource(url, entity, argumentsList[0])
      }
    })
  }
}
